import iconLogo from '../assets/img/logo.png'
import iconClose from '../assets/svg/iconClose.svg'
import iconOutbound from '../assets/svg/iconOutbound.svg'
import iconPlay from '../assets/svg/iconPlay.svg'
import iconReplay from '../assets/svg/iconReplay.svg'
import * as styles from '../constants/styles'
import sanitize from './sanitize'
import { Config, IPlaylistItem } from './config'

const getElementByClassName = (el: Element) => (name: string): HTMLElement => {
  return el.getElementsByClassName(name)[0] as HTMLElement
}

export class Components {
  private config: Config
  public dom: IComponents

  constructor(config: Config) {
    this.config = config
    this.dom = this.getComponents()

    const firstVideo = this.config.playlist[this.config.startingPosition]
    this.updateVideo(firstVideo)
    this.updateTitleBarPermalink(firstVideo.title, firstVideo.permalink)
  }

  public loadYoutubeBtn(dialog: 'end' | 'upnext'): void {
    const channelId = this.config.apiConfig.site.youtubeChannelId
    const btn = dialog === 'end'
      ? this.dom.dialogContainer.end.youtubeBtn
      : this.dom.dialogContainer.upNext.youtube.btn

    // no need to re-render
    if (btn.id.includes('ytsubscribe') || !channelId) {
      return
    }

    try {
      window.gapi.ytsubscribe.render(btn, {
        channelid: channelId,
        count: 'default',
        layout: 'full',
        theme: 'dark'
      })
    } catch (e) {
      console.error(e)
    }
  }

  public generateTitleBarElement(title: string, tag: string, permalink = ''): string {
    const sanitizedTitle = sanitize(title)
    const sanitizedPermalink = sanitize(permalink)
    const linkTextGuard = (text: string) => sanitizedPermalink ? text : ''

    const config = this.config
    const customColor = config.apiConfig.site.videoAdhesionBackground

    return `<${tag}
              class="${styles.TITLE_BAR_PERMALINK}"
              ${linkTextGuard(`href="${sanitizedPermalink}"`)}
              id="${styles.TITLE_BAR_PERMALINK}__${config.uuid}"
              style="fill: ${customColor};"
              ${linkTextGuard(`target="blank"`)}
            >
              ${linkTextGuard(`${iconOutbound}
              &nbsp;`)}
              ${sanitizedTitle}
            </${tag}>`
  }

  public getAgnosticPermalink(url: string) {
    return url.replace('http:', '').replace('https:', '')
  }

  public isCurrentPage(url: string): boolean {
    const permalink = this.getAgnosticPermalink(url)
    const currentPage = `//${window.location.host}${window.location.pathname}`

    return currentPage.includes(permalink)
  }

  public generateTileBarDom(title: string, permalink: string): string {
    return this.isCurrentPage(permalink)
    ? this.generateTitleBarElement(title, 'div')
    : this.generateTitleBarElement(title, 'a', permalink)
  }

  public updateTitleBarPermalink(title: string, permalink: string): void {
    this.dom.titleBar.name.innerHTML = this.generateTileBarDom(title, permalink)
  }

  public updateVideo(item: IPlaylistItem): void {
    this.dom.videoContainer.player.innerHTML = `
      ${item.sources.map((source) => `
        <source
          src="${sanitize(source.src)}"
          type="${sanitize(source.type)}"
        />
      `)}
    `
  }

  public updateUpNextDialog(poster: string, title: string, cb: Function): void {
    const { header, thumbnail } = this.dom.dialogContainer.upNext

    header.title.innerHTML = sanitize(title)
    thumbnail._el.style.backgroundImage = `url(${poster})`
    thumbnail.play.onclick = cb as any
  }

  private getComponents(): IComponents {
    const config = this.config

    if (!config.targetDiv) {
      throw new Error('Missing Target DIV')
    }

    const percentHeight = (1 / config.aspectFraction) * 100
    const pipPlacement = config.popoutPlacement.split('_')
    const vPipPlacement = `${styles.PREFIX}__${pipPlacement[0]}`
    const hPipPlacement = `${styles.PREFIX}__${pipPlacement[1]}`
    const hasLightTheme = config.apiConfig.site.videoAdhesionLightTheme
    const theme = hasLightTheme ? styles.THEME_LIGHT : styles.THEME_DARK
    const targetDiv = document.createElement('div')
    const getByClassName = getElementByClassName(targetDiv)

    config.targetDiv.appendChild(targetDiv)
    targetDiv.classList.add(`${styles.TARGET_DIV}`)
    targetDiv.id = `${styles.TARGET_DIV}__${config.uuid}`
    // add extra height for ad-reporter and title-bar, reduce for side margins
    targetDiv.style.paddingTop = `calc(${percentHeight}% + 66px - 4px)`
    targetDiv.innerHTML = `
      <div class="${styles.ROOT_CONTAINER}">
        <div class="${styles.PLACEMENT_CONTAINER} ${vPipPlacement} ${hPipPlacement} ${theme}">
          <div class="${styles.TITLE_BAR}">
            <div class="${styles.TITLE_BAR_NAME}">
              ${this.generateTileBarDom(config.title, config.permalink)}
            </div>

            <div class="${styles.TITLE_BAR_CLOSE_BTN} ${styles.ENABLED}">
              ${iconClose}
            </div>
          </div>

          <div class="${styles.VIDEO_CONTAINER} ${styles.HIDDEN}">
            <video
              class="${styles.PLAYER} video-js vjs-default-skin vjs-big-play-centered"
              id="${styles.PLAYER}__${config.uuid}"
              controls={true}
              height="${config.contentHeight}"
              muted="${config.muted}"
              playsInline
              poster="${config.apiConfig.video.thumbnail}"
              preload="${config.autoplay ? 'none' : 'metadata'}"
            ></video>
          </div>

          <div class="${styles.AD_REPORTER} ${styles.HIDDEN}">
            <a
              class="${styles.AD_REPORTER_LOGO}"
              href="https://www.mediavine.com"
              rel="noopener noreferrer"
              target="blank"
            >
              <img
                alt="Mediavine Logo"
                src="${iconLogo}"
              />
            </a>
            <button
              class="${styles.AD_REPORTER_BTN} mv-ad-report-button"
              data-target="${config.targetID}"
            >
              Report this Ad
            </button>
          </div>
        </div>
      </div>
    `

    return {
      _el: targetDiv as HTMLElement,
      adReporter: {
        _el: getByClassName(styles.AD_REPORTER),
        adReporterBtn: getByClassName(styles.AD_REPORTER_BTN),
        adReporterLogo: getByClassName(styles.AD_REPORTER_LOGO)
      },
      dialogContainer: {
        _el: getByClassName(styles.DIALOG),
        end: this.getDialogEnd(),
        upNext: this.getDialogUpNext()
      },
      placementContainer: getByClassName(styles.PLACEMENT_CONTAINER),
      rootContainer: getByClassName(styles.ROOT_CONTAINER),
      titleBar: {
        _el: getByClassName(styles.TITLE_BAR),
        closeBtn: getByClassName(styles.TITLE_BAR_CLOSE_BTN),
        name: getByClassName(styles.TITLE_BAR_NAME),
        permalink: getByClassName(styles.TITLE_BAR_PERMALINK)
      },
      videoContainer: {
        _el: getByClassName(styles.VIDEO_CONTAINER),
        player: getByClassName(styles.PLAYER),
        youtubeBtn: getByClassName(styles.YOUTUBE_BTN),
        youtubeBtnContainer: getByClassName(styles.YOUTUBE_BTN_CONTAINER)
      }
    }
  }

  private getDialogEnd(): IDialogEnd {
    const div = document.createElement('div')
    const getByClassName = getElementByClassName(div)

    div.className = styles.DIALOG_END
    div.innerHTML = `
      <div class="${styles.DIALOG_END_YOUTUBE}">
        <div class="${styles.YOUTUBE_BTN}"></div>
      </div>
      <div class="${styles.DIALOG_END_REPLAY}">
        ${iconReplay} Replay
      </div>
    `

    return {
      _el: div,
      replay: getByClassName(styles.DIALOG_END_REPLAY),
      youtubeBtn: getByClassName(styles.YOUTUBE_BTN),
      youtubeBtnContainer: getByClassName(styles.DIALOG_END_YOUTUBE)
    }
  }

  private getDialogUpNext(): IDialogUpNext {
    const div = document.createElement('div')
    const getByClassName = getElementByClassName(div)

    div.className = styles.DIALOG_UPNEXT
    div.innerHTML = `
      <div class="${styles.DIALOG_UPNEXT_HEADER}">
        <div class="${styles.DIALOG_UPNEXT_SUBTITLE}">Up Next:</div>
        <div class="${styles.DIALOG_UPNEXT_TITLE}"></div>
      </div>
      <div class="${styles.DIALOG_UPNEXT_THUMBNAIL}">
        <div class="${styles.DIALOG_UPNEXT_THUMBNAIL_PLAY}">
          ${iconPlay}
        </div>
      </div>
      <div class="${styles.DIALOG_UPNEXT_COUNTDOWN}">
        <div class="${styles.DIALOG_UPNEXT_COUNTDOWN_REMAINING}">5</div>
        <div class="${styles.DIALOG_UPNEXT_COUNTDOWN_ACTION}">Cancel</div>
        <div class="${styles.DIALOG_UPNEXT_COUNTDOWN_REPLAY}">
          ${iconReplay}
        </div>
      </div>
      <div class="${styles.DIALOG_UPNEXT_YOUTUBE}">
        <div class="${styles.YOUTUBE_BTN}"></div>
      </div>
    `

    return {
      _el: div,
      countdown: {
        _el: getByClassName(styles.DIALOG_UPNEXT_COUNTDOWN),
        action: getByClassName(styles.DIALOG_UPNEXT_COUNTDOWN_ACTION),
        remaining: getByClassName(styles.DIALOG_UPNEXT_COUNTDOWN_REMAINING),
        replay: getByClassName(styles.DIALOG_UPNEXT_COUNTDOWN_REPLAY)
      },
      header: {
        _el: getByClassName(styles.DIALOG_UPNEXT_HEADER),
        subtitle: getByClassName(styles.DIALOG_UPNEXT_SUBTITLE),
        title: getByClassName(styles.DIALOG_UPNEXT_TITLE)
      },
      thumbnail: {
        _el: getByClassName(styles.DIALOG_UPNEXT_THUMBNAIL),
        play: getByClassName(styles.DIALOG_UPNEXT_THUMBNAIL)
      },
      youtube: {
        _el: getByClassName(styles.DIALOG_UPNEXT_YOUTUBE),
        btn: getByClassName(styles.YOUTUBE_BTN)
      }
    }
  }
}

interface IComponents {
  _el: HTMLElement
  adReporter: {
    _el: HTMLElement
    adReporterBtn: HTMLElement
    adReporterLogo: HTMLElement
  }
  dialogContainer: {
    _el: HTMLElement
    end: IDialogEnd
    upNext: IDialogUpNext
  }
  placementContainer: HTMLElement
  rootContainer: HTMLElement
  titleBar: {
    _el: HTMLElement
    closeBtn: HTMLElement
    name: HTMLElement
    permalink: HTMLElement
  }
  videoContainer: {
    _el: HTMLElement
    player: HTMLElement
    youtubeBtn: HTMLElement
    youtubeBtnContainer: HTMLElement
  }
}

interface IDialogEnd {
  _el: HTMLElement
  replay: HTMLElement
  youtubeBtn: HTMLElement
  youtubeBtnContainer: HTMLElement
}

interface IDialogUpNext {
  _el: HTMLElement
  countdown: {
    _el: HTMLElement
    action: HTMLElement
    remaining: HTMLElement
    replay: HTMLElement
  }
  header: {
    _el: HTMLElement
    subtitle: HTMLElement
    title: HTMLElement
  }
  thumbnail: {
    _el: HTMLElement
    play: HTMLElement
  }
  youtube: {
    _el: HTMLElement
    btn: HTMLElement
  }
}
